import { useEffect } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { useAuth, useInquiryInbox } from "@bw/shared/hooks";

export function CoachInquiryPreview() {
  const { user } = useAuth();
  const query = useInquiryInbox(user?.uid, {
    scope: "mine",
    lifecycle: "open",
    limit: 5,
  });
  const rows = [
    ...new Map(
      (query.data?.pages.flatMap((page) => page.items) ?? []).map((item) => [
        item.conversation_id,
        item,
      ]),
    ).values(),
  ];

  useEffect(() => {
    if (
      query.isSuccess &&
      rows.length === 0 &&
      query.hasNextPage &&
      !query.isFetchingNextPage
    ) {
      void query.fetchNextPage();
    }
  }, [query, rows.length]);

  return (
    <Paper
      aria-label="Player messages"
      component="section"
      variant="outlined"
      sx={{ overflow: "hidden" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{
          px: 3,
          py: 2.5,
          alignItems: { sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Stack spacing={0.25}>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            Player messages
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Open a player conversation to reply or send a proposal.
          </Typography>
        </Stack>
        <Button component={RouterLink} to="/inquiries" size="small">
          Open inbox
        </Button>
      </Stack>
      {query.isError ? (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void query.refetch()}
            >
              Retry
            </Button>
          }
          sx={{ mx: 3, mb: 2.5 }}
        >
          We couldn&apos;t load player messages.
        </Alert>
      ) : query.isPending || (query.isFetchingNextPage && rows.length === 0) ? (
        <Typography
          color="text.secondary"
          role="status"
          sx={{ px: 3, pb: 2.5 }}
        >
          Loading player messages…
        </Typography>
      ) : rows.length ? (
        <List disablePadding>
          {rows.slice(0, 5).map((item) => (
            <ListItemButton
              component={RouterLink}
              divider
              key={item.conversation_id}
              to={`/inquiries/${encodeURIComponent(item.conversation_id)}`}
              sx={{ px: 3, py: 1.5 }}
            >
              <ListItemText
                primary={item.topic}
                secondary={`${item.requester_name ?? "Player"} · ${item.facility_name ?? item.target_name ?? "Coaching inquiry"}`}
                slotProps={{ primary: { sx: { fontWeight: 700 } } }}
              />
              {item.unread_count > 0 ? (
                <Chip
                  color="primary"
                  size="small"
                  label={`${item.unread_count} unread`}
                />
              ) : null}
            </ListItemButton>
          ))}
        </List>
      ) : (
        <Typography color="text.secondary" sx={{ px: 3, pb: 2.5 }}>
          No open player messages assigned or addressed to you.
        </Typography>
      )}
    </Paper>
  );
}
